import { Alert, Button, Modal } from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeaconTableColumns } from "../../api";
import { Beacon, SCBOneDevice } from "../../api/models";
import { PagedApiState } from "../../api/useApiState";
import { BeaconTableBase } from "../BeaconTableBase";

interface SelectBeaconsModalProps {
  modalButton?: (openModal: () => void) => React.ReactNode;
  modalButtonIcon?: React.ReactNode;
  title: string;
  instruction: string;
  selectButtonText(beaconIds: string[]): string;
  selectButtonTitle(beaconIds: string[]): string;
  assignedToOrganization?: boolean;
  onBeaconsSelected(beaconIds: string[]): Promise<any>;
  beacons: PagedApiState<Beacon | SCBOneDevice>;
  showPagination?: boolean;
}

export function SelectBeaconsModal(props: SelectBeaconsModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBeaconIds, setSelectedBeaconIds] = useState<string[]>([]);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const onSelect = async () => {
    setIsSelecting(true);
    try {
      await props.onBeaconsSelected(selectedBeaconIds);
    } catch (error) {
      setIsSelecting(false);
      setError(
        error instanceof Error ? error.message : t("translations:error-save")
      );
      return;
    }
    setIsOpen(false);
    setIsSelecting(false);
    setSelectedBeaconIds([]);
  };

  const onCancel = () => {
    if (isSelecting) {
      return;
    }
    setIsOpen(false);
    setIsSelecting(false);
    setError(null);
    setSelectedBeaconIds([]);
  };

  const modalFooter = [
    <Button key="cancel" disabled={isSelecting} onClick={onCancel}>
      {t("cancel")}
    </Button>,
    <Button
      key="select"
      disabled={isSelecting || !selectedBeaconIds.length}
      loading={isSelecting}
      onClick={onSelect}
      title={props.selectButtonTitle(selectedBeaconIds)}
      type="primary"
    >
      {props.selectButtonText(selectedBeaconIds)}
    </Button>,
  ];

  return (
    <>
      {props.modalButton ? (
        props.modalButton(openModal)
      ) : (
        <Button onClick={openModal} type="primary">
          {props.modalButtonIcon}
          {props.title}
        </Button>
      )}
      <Modal
        width="50%"
        style={{ top: Math.max(16, window.innerHeight * 0.15 - 77) }}
        bodyStyle={{ overflowY: "auto" }}
        title={props.title}
        open={isOpen}
        onCancel={onCancel}
        onOk={onSelect}
        footer={modalFooter}
        destroyOnClose
      >
        <div
          data-testid="assign-beacons-modal"
          style={{ height: window.innerHeight * 0.7 }}
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <BeaconTableBase
            visibleColumns={[
              BeaconTableColumns.Serial,
              BeaconTableColumns.Status,
            ]}
            beaconData={props.beacons}
            rowsSelectable={true}
            onSelect={setSelectedBeaconIds}
            showSorter={false}
            showPagination={props.showPagination}
          />
        </div>
      </Modal>
    </>
  );
}